import { InfoTip } from "@/design-system/InfoTip/InfoTip";
import { ItemScoreDiamond } from "@/design-system/ItemScore/ItemScoreDiamond";
import { Popover } from "@/design-system/Popover/Popover";
import { CoinIcon } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import type { GalleryLevelPlan } from "@repo/shared/api";
import { type LevelPlanStatKey, levelPlanStats } from "@repo/shared/gallery";
import { useTranslation } from "@repo/shared/i18n";
import {
  ArrowLeftRight,
  ChevronDown,
  Layers,
  TriangleAlert,
} from "lucide-react";
import { ReactNode } from "react";

const ASSETS_BASE_URL = import.meta.env.VITE_APP_ASSETS_BASE_URL;

type Stat = {
  key: LevelPlanStatKey;
  label: string;
  icon?: ReactNode;
  value: string;
  /** Muted after the value: the target or limit it's measured against. */
  of?: string;
  /** Amber: the plan falls short here. */
  short?: boolean;
  /** Why it's short, in the info tip beside it. */
  note?: string;
};

const StatValue = ({ stat }: { stat: Stat }) => (
  <span className="flex items-center gap-1 whitespace-nowrap font-oswald tabular-nums">
    {stat.icon}
    <span
      className={cn(
        "text-base font-medium",
        stat.short ? "text-amber-400" : "text-neutral-100",
      )}
    >
      {stat.value}
    </span>
    {stat.of && (
      <span
        className={cn(
          "text-sm",
          stat.short ? "text-amber-400" : "text-neutral-500",
        )}
      >
        / {stat.of}
      </span>
    )}
  </span>
);

/**
 * The built plan's totals, small, under its heading: label-over-value figures
 * in a row from mobile up; on phones one figure and the rest behind a popover,
 * like My Club's club value.
 */
export const GalleryLevelPlanSummary = ({
  plan,
  targetScore,
  coinBalance,
}: {
  plan: GalleryLevelPlan;
  targetScore?: number;
  /** The plan's coin limit, else the last Club Import's coins. */
  coinBalance?: number;
}) => {
  const { t } = useTranslation("gallery");
  const icon = (src: string) => (
    <img className="size-3.5" src={`${ASSETS_BASE_URL}/misc/${src}`} alt="" />
  );
  const icons: Record<LevelPlanStatKey, ReactNode> = {
    score: <ItemScoreDiamond size={13} />,
    coinsNeeded: <CoinIcon size={13} />,
    taxLoss: <CoinIcon size={13} />,
    transfers: <ArrowLeftRight className="size-3.5 text-neutral-400" />,
    sets: <Layers className="size-3.5 text-neutral-400" />,
    sbcPrice: icon("sbc-tmp.png"),
    objectivePlayers: icon("obj-tmp.png"),
  };
  const stats: Stat[] = levelPlanStats(plan, targetScore, coinBalance).map(
    (stat) => ({
      ...stat,
      label: t(`levels.results.${stat.key}`),
      icon: icons[stat.key],
      note: !stat.short
        ? undefined
        : stat.key === "score" && stat.of
          ? t("levels.summary.scoreShort", {
              score: stat.value,
              target: stat.of,
            })
          : stat.key === "coinsNeeded" && stat.of
            ? t("levels.summary.coinsShort", {
                balance: stat.of,
                needed: stat.value,
              })
            : undefined,
    }),
  );
  const [score, , taxLoss] = stats;

  return (
    <>
      {/* From mobile up: every figure in its own tile, the row wrapping as space runs out. */}
      <div className="flex flex-col gap-2 max-mobile:hidden">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(9rem,1fr))] gap-2.5">
          {stats.map((stat) => (
            <div
              key={stat.key}
              className="flex min-w-0 flex-col gap-1 rounded-xl bg-neutral-750 px-3 py-2.5"
            >
              <span className="truncate text-xs text-neutral-400">
                {stat.label}
              </span>
              <span className="flex items-center gap-1.5">
                <StatValue stat={stat} />
                {stat.note && (
                  <InfoTip
                    label={stat.note}
                    width={280}
                    icon={<TriangleAlert className="size-3.5" />}
                    className="text-amber-400 hover:text-amber-300"
                  >
                    {stat.note}
                  </InfoTip>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
      {/* Phones: one full-width card with score and tax loss, the rest behind it like My Club's club value. */}
      <div className="mobile:hidden">
        <Popover
          placement="bottom"
          align="start"
          width={280}
          newDesignSystem
          negLabel=""
          posLabel=""
          // A header row for the close button, so it doesn't cover the first figure.
          title={t("levels.summary.title", { level: plan.level })}
          trigger={
            <span className="flex w-full flex-col gap-2 rounded-xl bg-neutral-750 px-3 py-2.5 text-left">
              <span className="flex items-center gap-3">
                <span className="grid flex-1 grid-cols-2 gap-x-4">
                  {[score, taxLoss].map((stat) => (
                    <span key={stat.key} className="flex flex-col gap-1">
                      <span className="text-xs text-neutral-400">
                        {stat.label}
                      </span>
                      <StatValue stat={stat} />
                    </span>
                  ))}
                </span>
                <ChevronDown
                  className="size-4 shrink-0 text-neutral-400"
                  strokeWidth={2.4}
                />
              </span>
            </span>
          }
        >
          <div className="flex flex-col gap-3">
            {stats.map((stat) => (
              <div key={stat.key} className="flex flex-col gap-1">
                <div className="flex items-center gap-2.5">
                  <span className="text-xxs uppercase tracking-wider text-foreground-muted">
                    {stat.label}
                  </span>
                  <span className="ml-auto">
                    <StatValue stat={stat} />
                  </span>
                </div>
                {stat.note && (
                  <span className="text-xs text-amber-400">{stat.note}</span>
                )}
              </div>
            ))}
          </div>
        </Popover>
      </div>
    </>
  );
};
